// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { getApiBaseUrl } from '@/services/apiBaseUrl';
import { createAppLogger } from '@/services/appLogger';
import { buildAccessHeaders } from '@/services/claimHttp';
import { getSharedBffWebSocket, type BffInboundMessage } from '@/services/bffWebSocket';

import { PHOTO_UPLOAD_TIMEOUT_MS } from './constants';
import type { PickedPhoto } from './types';

/**
 * Module-scoped upload queue for tour photos.
 *
 * Deliberately NOT a hook. The primary capture surface is the hole screen, and that screen unmounts
 * the moment a player advances to the next hole — which is exactly when someone who just took a
 * photo is most likely to move on. Holding upload state in component state would cancel their
 * upload; holding it here means the request outlives the screen that started it.
 *
 * The protocol matches the tour cover image: presign over the WebSocket, PUT the bytes straight to
 * S3, then persist the row over HTTP. See useTourEditData for the original.
 */

export type PhotoUploadJob = {
  id: string;
  tourId: string;
  roundId: string | null;
  holeNumber: number | null;
  caption: string | null;
  photo: PickedPhoto;
};

export type PhotoUploadState = {
  /** Jobs currently being uploaded, oldest first. */
  pending: PhotoUploadJob[];
  /** Set when the most recent job failed; cleared on the next enqueue. */
  lastError: string | null;
};

type Listener = (state: PhotoUploadState) => void;

let pending: PhotoUploadJob[] = [];
let lastError: string | null = null;
let draining = false;
const listeners = new Set<Listener>();

const snapshot = (): PhotoUploadState => ({ pending: [...pending], lastError });

const emit = () => {
  const state = snapshot();
  listeners.forEach((listener) => listener(state));
};

export const subscribeToPhotoUploads = (listener: Listener): (() => void) => {
  listeners.add(listener);
  listener(snapshot());
  return () => {
    listeners.delete(listener);
  };
};

export const getPhotoUploadState = snapshot;

/**
 * Queues a photo. Returns immediately — "shoot and go": nobody wants a progress modal while
 * standing on the tee. Progress and failures are observable via {@link subscribeToPhotoUploads}.
 */
export const enqueuePhotoUpload = (job: PhotoUploadJob): void => {
  pending = [...pending, job];
  lastError = null;
  emit();
  void drain();
};

/** Uploads are sequenced, never parallel: ten 5 MB photos over course mobile data would thrash. */
const drain = async (): Promise<void> => {
  if (draining) {
    return;
  }
  draining = true;

  try {
    while (pending.length > 0) {
      const job = pending[0];
      try {
        await uploadOne(job);
      } catch (cause) {
        lastError = cause instanceof Error ? cause.message : 'photo upload failed';
      } finally {
        pending = pending.filter((entry) => entry.id !== job.id);
        emit();
      }
    }
  } finally {
    draining = false;
  }
};

type PresignAck = {
  uploadUrl: string;
  key: string;
};

const uploadOne = async (job: PhotoUploadJob): Promise<void> => {
  const apiBaseUrl = getApiBaseUrl();
  const token = getAuthToken();
  if (!token) {
    throw new Error('photo upload requires a signed-in user');
  }

  // Every step logs. The upload spans a WebSocket round-trip, a direct-to-S3 PUT and an HTTP POST,
  // and on a phone none of that is inspectable — without breadcrumbs a failure anywhere in the
  // chain looks identical from the outside: the photo simply never appears.
  const log = createAppLogger(apiBaseUrl, token, { source: 'tour-photo-upload' });
  log('photo upload start', {
    photoId: job.id,
    tourId: job.tourId,
    roundId: job.roundId,
    holeNumber: job.holeNumber,
    contentType: job.photo.contentType,
    byteSize: job.photo.byteSize,
    blobSize: job.photo.blob?.size ?? null,
  });

  const { uploadUrl, key } = await requestPresign(apiBaseUrl, job, token);
  log('photo upload presigned', { photoId: job.id, key });

  const putResponse = await fetch(uploadUrl, {
    method: 'PUT',
    headers: {
      'Content-Type': job.photo.contentType,
      // Byte-identical to what the BFF signed into the presigned URL. S3 rejects the PUT with
      // SignatureDoesNotMatch if this differs at all — see S3TourImageStorage.
      'Cache-Control': 'public, max-age=31536000, immutable',
    },
    body: job.photo.blob,
  });

  if (!putResponse.ok) {
    // The body carries S3's actual complaint; without it a rejected upload is just "failed".
    const body = await putResponse.text().catch(() => '');
    log(
      'photo upload PUT rejected',
      { photoId: job.id, status: putResponse.status, body: body.slice(0, 400) },
      'error',
    );
    throw new Error(`upload failed with ${putResponse.status} ${body.slice(0, 200)}`);
  }

  log('photo upload bytes stored', { photoId: job.id, key });

  const createResponse = await fetch(`${apiBaseUrl}/api/tours/${encodeURIComponent(job.tourId)}/photos`, {
    method: 'POST',
    headers: { ...buildAccessHeaders(token), 'Content-Type': 'application/json' },
    body: JSON.stringify({
      photoId: job.id,
      imageKey: key,
      contentType: job.photo.contentType,
      byteSize: job.photo.byteSize,
      caption: job.caption,
      roundId: job.roundId,
      holeNumber: job.holeNumber,
    }),
  });

  if (!createResponse.ok) {
    const body = await createResponse.text().catch(() => '');
    log(
      'photo upload create rejected',
      { photoId: job.id, status: createResponse.status, body: body.slice(0, 400) },
      'error',
    );
    throw new Error(`saving the photo failed with ${createResponse.status} ${body.slice(0, 200)}`);
  }

  log('photo upload done', { photoId: job.id });
};

const requestPresign = (apiBaseUrl: string, job: PhotoUploadJob, token: string): Promise<PresignAck> => {
  const socket = getSharedBffWebSocket(apiBaseUrl);
  socket.setAuthToken(token);

  return new Promise<PresignAck>((resolve, reject) => {
    let settled = false;
    const finish = (fn: () => void) => {
      if (settled) {
        return;
      }
      settled = true;
      clearTimeout(timeout);
      unsubscribe();
      fn();
    };

    const timeout = setTimeout(
      () => finish(() => reject(new Error('tour.photo.presign timed out'))),
      PHOTO_UPLOAD_TIMEOUT_MS,
    );

    const unsubscribe = socket.onMessage((inbound: BffInboundMessage) => {
      // Read through a local view rather than narrowing the union: TourUpdateEvent declares
      // `type?: string`, so a discriminated check never excludes it and the fields stay unreachable.
      const message = inbound as {
        type?: string;
        photoId?: string;
        uploadUrl?: string;
        key?: string;
        message?: string;
      };

      if (message.type !== 'tour.photo.presign.ack' && message.type !== 'tour.photo.presign.error') {
        return;
      }

      // The socket is shared, so several uploads can be presigning at once; photoId pairs an ack
      // with its job.
      if (message.photoId !== job.id) {
        return;
      }

      if (message.type === 'tour.photo.presign.ack') {
        const { uploadUrl, key } = message;
        if (!uploadUrl || !key) {
          finish(() => reject(new Error('tour.photo.presign.ack was missing uploadUrl or key')));
          return;
        }
        finish(() => resolve({ uploadUrl, key }));
        return;
      }

      finish(() => reject(new Error(message.message ?? 'presign rejected')));
    });

    // 'queued' is not a failure — the frame flushes when the socket reopens, and the timeout above
    // is the only thing that gives up on an unanswered presign.
    socket.send({
      type: 'tour.photo.presign',
      tourId: job.tourId,
      photoId: job.id,
      requestId: job.id,
      fileName: job.photo.fileName,
      contentType: job.photo.contentType,
      fileSize: job.photo.byteSize,
    });
  });
};

// The queue outlives any React tree, so it cannot read the token from useUser(). The screens that
// enqueue set it, exactly like the socket's own auth token.
let authToken: string | null = null;

export const setPhotoUploadAuthToken = (token: string | null | undefined): void => {
  authToken = token?.trim() || null;
};

const getAuthToken = (): string | null => authToken;

/** Test seam. */
export const __resetPhotoUploadQueue = (): void => {
  pending = [];
  lastError = null;
  draining = false;
  listeners.clear();
  authToken = null;
};
